import { IResource } from "@gettactic/api";

export interface SelectFloorOptions {
  /** All available floors */
  allFloors: IResource[] | undefined;
  /** Whether to include floors without maps */
  getAllMaps?: boolean;
  /** Initial floor ID from URL/props (highest priority) */
  initialFloorId?: string | null;
  /** Controlled floor ID from props */
  controlledFloorId?: string | null;
  /** Last used floor ID from storage */
  lastFloorId?: string | null;
  /** User's default area ID from profile */
  defaultAreaId?: string | null;
  /** Currently selected floor ID */
  currentFloorId?: string | null;
  /** If true, allows returning null when no floor matches (fewer fallbacks) */
  allowNoFloor?: boolean;
}

export interface SelectFloorResult {
  /** The selected floor, or null if no floor could be selected */
  floor: IResource | null;
  /** The list of floors with maps (filtered from allFloors) */
  floorsWithMap: IResource[];
  /** Reason for the selection (useful for debugging) */
  selectionReason:
    | "single_floor"
    | "initial_floor_id"
    | "controlled_floor_id"
    | "last_floor_id"
    | "default_area_id"
    | "current_floor_id"
    | "first_floor_with_map"
    | "first_floor"
    | "no_floors"
    | "no_match";
}

/**
 * Pure function to select the appropriate floor based on various criteria.
 *
 * Priority order:
 * 1. If only one floor exists, select it
 * 2. initialFloorId (from URL or props - highest explicit priority)
 * 3. controlledFloorId (controlled prop)
 * 4. lastFloorId (from localStorage)
 * 5. defaultAreaId (user's default from profile) - only when allowNoFloor is false
 * 6. currentFloorId (preserve current selection)
 * 7. First floor with a map
 * 8. First floor (any) - only when allowNoFloor is false
 * 9. null - when allowNoFloor is true and nothing matches
 */
export function selectFloor(options: SelectFloorOptions): SelectFloorResult {
  const {
    allFloors,
    getAllMaps = false,
    initialFloorId,
    controlledFloorId,
    lastFloorId,
    defaultAreaId,
    currentFloorId,
    allowNoFloor = false
  } = options;

  // No floors available
  if (!allFloors || allFloors.length === 0) {
    return {
      floor: null,
      floorsWithMap: [],
      selectionReason: "no_floors"
    };
  }

  // Single floor - always select it
  if (allFloors.length === 1) {
    return {
      floor: allFloors[0],
      floorsWithMap: allFloors,
      selectionReason: "single_floor"
    };
  }

  // Filter floors with maps
  const floorsWithMap = getAllMaps
    ? allFloors
    : allFloors.filter((floor) => !!floor.map_url || !!floor.map_data);

  // Helper to find floor by ID
  const findFloor = (id: string | null | undefined): IResource | undefined => {
    if (!id) return undefined;
    return floorsWithMap.find((floor) => floor.id === id);
  };

  // Try each priority in order

  // 1. initialFloorId (highest explicit priority)
  const initialFloor = findFloor(initialFloorId);
  if (initialFloor) {
    return {
      floor: initialFloor,
      floorsWithMap,
      selectionReason: "initial_floor_id"
    };
  }

  // 2. controlledFloorId
  const controlledFloor = findFloor(controlledFloorId);
  if (controlledFloor) {
    return {
      floor: controlledFloor,
      floorsWithMap,
      selectionReason: "controlled_floor_id"
    };
  }

  // 3. lastFloorId (from storage)
  const lastFloor = findFloor(lastFloorId);
  if (lastFloor) {
    return {
      floor: lastFloor,
      floorsWithMap,
      selectionReason: "last_floor_id"
    };
  }

  // 4. defaultAreaId (only when allowNoFloor is false)
  if (!allowNoFloor) {
    const defaultFloor = findFloor(defaultAreaId);
    if (defaultFloor) {
      return {
        floor: defaultFloor,
        floorsWithMap,
        selectionReason: "default_area_id"
      };
    }
  }

  // 5. currentFloorId (preserve current selection)
  const currentFloor = findFloor(currentFloorId);
  if (currentFloor) {
    return {
      floor: currentFloor,
      floorsWithMap,
      selectionReason: "current_floor_id"
    };
  }

  // 6. First floor with a map
  if (floorsWithMap.length > 0) {
    return {
      floor: floorsWithMap[0],
      floorsWithMap,
      selectionReason: "first_floor_with_map"
    };
  }

  // 7. First floor (any) - only when allowNoFloor is false
  if (!allowNoFloor && allFloors.length > 0) {
    return {
      floor: allFloors[0],
      floorsWithMap,
      selectionReason: "first_floor"
    };
  }

  // 8. No match found
  return {
    floor: null,
    floorsWithMap,
    selectionReason: "no_match"
  };
}

/**
 * Parses floor ID from URL query parameters.
 * Handles both query string and hash-based URL formats.
 *
 * @param queryFloorId - The floorId from router.query
 * @param hashString - The hash portion of the URL (without #)
 * @returns The parsed floor ID or null
 */
export function parseFloorIdFromUrl(
  queryFloorId: string | string[] | undefined,
  hashString?: string
): string | null {
  // First try query parameter
  if (queryFloorId) {
    if (Array.isArray(queryFloorId)) {
      return queryFloorId[0] || null;
    }
    return queryFloorId;
  }

  // Then try hash parameter (for legacy URL support)
  if (hashString) {
    const parsedHash = new URLSearchParams(hashString);
    return parsedHash.get("floorId");
  }

  return null;
}
